import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL, buildKioskAuthHeaders } from './api';
import { getOfflineAccountScope, isOfflineScopeCurrent } from './scopedOfflineQueue';

function formatTicket(ticketNumber) {
  return `#${String(ticketNumber).padStart(4, '0')}`;
}

function EtatView() {
  const [accountScope] = useState(getOfflineAccountScope);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      if (!isOfflineScopeCurrent(accountScope)) {
        setOrders([]);
        throw new Error('Compte modifié : rechargez l’affichage des commandes.');
      }
      const response = await fetch(`${API_URL}/api/orders`, {
        headers: buildKioskAuthHeaders(),
        cache: 'no-store',
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) setOrders([]);
        throw new Error('Impossible de charger les commandes.');
      }
      const payload = await response.json();
      if (!isOfflineScopeCurrent(accountScope)) {
        setOrders([]);
        throw new Error('Compte modifié : rechargez l’affichage des commandes.');
      }
      setOrders(Array.isArray(payload) ? payload : []);
      setError('');
    } catch (fetchError) {
      setError(fetchError.message);
    }
  }, [accountScope]);

  useEffect(() => {
    fetchOrders();
    const intervalId = window.setInterval(fetchOrders, 10000);
    return () => window.clearInterval(intervalId);
  }, [fetchOrders]);

  const preparingOrders = useMemo(
    () =>
      orders
        .filter((order) => order.status === 'en_cours')
        .sort((a, b) => new Date(a.orderedAt) - new Date(b.orderedAt)),
    [orders]
  );

  const readyOrders = useMemo(
    () =>
      orders
        .filter((order) => order.status === 'prete')
        .sort((a, b) => new Date(a.readyAt || a.orderedAt) - new Date(b.readyAt || b.orderedAt)),
    [orders]
  );

  return (
    <div className="etat-page">
      <header className="etat-header">
        <h1>État des commandes</h1>
      </header>
      {error ? <div className="panel-alert" role="alert">{error}</div> : null}
      <div className="etat-grid">
        <section className="etat-column">
          <div className="etat-column-head">
            <h2>En préparation</h2>
            <strong>{preparingOrders.length}</strong>
          </div>
          <div className="etat-list">
            {preparingOrders.length ? (
              preparingOrders.map((order) => (
                <article key={order.id} className="etat-ticket is-preparing">
                  <span>Commande</span>
                  <strong>{formatTicket(order.ticketNumber || order.id)}</strong>
                </article>
              ))
            ) : (
              <div className="etat-empty">Aucune commande en préparation.</div>
            )}
          </div>
        </section>
        <section className="etat-column">
          <div className="etat-column-head">
            <h2>Prêtes</h2>
            <strong>{readyOrders.length}</strong>
          </div>
          <div className="etat-list">
            {readyOrders.length ? (
              readyOrders.map((order) => (
                <article key={order.id} className="etat-ticket is-ready">
                  <span>Commande</span>
                  <strong>{formatTicket(order.ticketNumber || order.id)}</strong>
                </article>
              ))
            ) : (
              <div className="etat-empty">Aucune commande prête.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default EtatView;
